import { Body, Button, Icon } from '@assembly-js/design-system';
import React from 'react';

import { useMediaQuery } from 'src/hooks/useMediaQuery';
import type { ModalWrapperProps } from 'src/legacy/components/Modals/ModalWrapper';

/**
 * The centered card width every plan-change modal uses on desktop: Figma
 * draws 470px of content inside the 20px inset and the card's 1px border
 * (7122:59712, 7127:61230, 6952:112075, 6952:112604 are all 512).
 */
export const PLAN_CHANGE_MODAL_DESKTOP_WIDTH = 512;

// The heading sits on the same inset and rhythm as the body, where
// ModalWrapper's own title padding is a flat 16/28 — its mobile override is
// keyed to `down('xs')`, which resolves to `max-width: -0.05px` in MUI v5 and
// never matches. So: 20px all round on desktop, and on the sheet 16px sides
// over a 12px top with 16px down to the content (Figma 7130:62080).
const TITLE_INSET_SX = { px: 2.5, pt: 2.5, pb: 2.5 };
const MOBILE_TITLE_INSET_SX = { px: 2, pt: 1.5, pb: 2 };

// ModalWrapper's heading is 15/24 over 13/20, the app-wide default; Figma
// gives this family headingLg 16/24 over bodyMd 14/22 (7122:59713). Restyled
// per modal rather than in the wrapper, which every other modal shares.
const TITLE_TYPOGRAPHY_SX = {
  '& .MuiDialogTitle-root > div:first-of-type': {
    fontSize: '16px',
    lineHeight: '24px',
  },
  '& .MuiDialogTitle-root > div:nth-of-type(2)': {
    fontSize: '14px',
    lineHeight: '22px',
  },
};

// Figma hangs the close glyph off a 24px icon button (28px on the sheet) sat
// on the content inset, so the 16px glyph lands at inset + the button's own
// padding — 20+5 on desktop, 16+6 on the sheet. ModalWrapper defaults to
// 18/18 for every modal in the app.
const DESKTOP_CLOSE_SX = {
  '& .MuiDialogTitle-root > button': { right: '25px', top: '25px' },
};
const MOBILE_CLOSE_SX = {
  '& .MuiDialogTitle-root > button': { right: '22px', top: '18px' },
};

const DESKTOP_SX = {
  '& .MuiDialogTitle-root': TITLE_INSET_SX,
  ...TITLE_TYPOGRAPHY_SX,
  ...DESKTOP_CLOSE_SX,
};

const MOBILE_SHEET_SX = {
  '& .MuiDialog-paper': {
    m: 0,
    // Explicit, because ModalWrapper's own `fullWidth` rule resolves to
    // `width: auto` from MUI's 600px up — this sheet starts at 640px.
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    border: 'none',
    borderRadius: 0,
  },
  '& .MuiDialogTitle-root': MOBILE_TITLE_INSET_SX,
  ...TITLE_TYPOGRAPHY_SX,
  ...MOBILE_CLOSE_SX,
};

type StudioModalSheetProps = Pick<
  ModalWrapperProps,
  'fullWidth' | 'width' | 'height' | 'sx'
>;

/**
 * ModalWrapper props that pin the heading to the Figma inset and, below `sm`,
 * turn the modal into a full-screen sheet (the 375px frames 7130:61938 /
 * 61997 / 62027 / 62080 / 62166). Left alone, MUI centers the desktop card
 * and shrinks it to fit inside its 32px margins — about 310px of usable width
 * on a phone, too narrow for the cards, credit selectors, and side-by-side
 * CTAs.
 */
export const useStudioModalSheetProps = (
  desktopWidth: number = PLAN_CHANGE_MODAL_DESKTOP_WIDTH,
): StudioModalSheetProps => {
  const isMobile = useMediaQuery('sm', 'max');
  return isMobile
    ? { fullWidth: true, height: '100%', sx: MOBILE_SHEET_SX }
    : { width: desktopWidth, sx: DESKTOP_SX };
};

/**
 * A check bullet and its label. Figma seats the 16px glyph in a line-height
 * box so it optically centers on the first line rather than the whole block.
 */
export const PlanChangeBullet: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="flex w-full items-start gap-1.5">
    <div className="flex h-[22px] shrink-0 items-center">
      <Icon icon="Check" className="h-4 w-4 text-black-heading" />
    </div>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

/** A card's price line: the amount with its interval/due-date suffix. */
export const PlanChangePrice: React.FC<{ amount: string; suffix: string }> = ({
  amount,
  suffix,
}) => (
  <div className="flex items-baseline gap-1.5">
    <span className="text-2xl font-medium leading-8 text-black-heading">
      {amount}
    </span>
    <Body size="sm" tag="span" className="text-black-heading">
      {suffix}
    </Body>
  </div>
);

interface PlanChangeModalButtonsProps {
  cancelLabel: string;
  confirmLabel: string;
  isLoading: boolean;
  isConfirmDisabled?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * The Figma full-width two-button row: Cancel and confirm split the width.
 * `sm` is the 28px button the frames draw; at the default 32px the longer
 * labels wrap to a second line once the row is halved on a phone.
 */
export const PlanChangeModalButtons: React.FC<PlanChangeModalButtonsProps> = ({
  cancelLabel,
  confirmLabel,
  isLoading,
  isConfirmDisabled = false,
  onCancel,
  onConfirm,
}) => (
  <div className="flex gap-3">
    <Button
      type="button"
      variant="secondary"
      size="sm"
      className="flex-1"
      label={cancelLabel}
      disabled={isLoading}
      onClick={onCancel}
    />
    <Button
      type="button"
      variant="primary"
      size="sm"
      className="flex-1"
      label={confirmLabel}
      disabled={isConfirmDisabled || isLoading}
      loading={isLoading}
      onClick={onConfirm}
    />
  </div>
);
